import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Search, FileCheck, DollarSign, BarChart3,
  Shield, Clock, Users, Zap, Send, TrendingUp,
  FolderOpen, Scale, Check, Briefcase,
} from 'lucide-react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import styles from '@/styles/partnerPage.module.css';

const ASSURANCES = [
  { icon: FileCheck, label: 'Attorney-Verified' },
  { icon: Zap, label: 'Fast Underwriting' },
  { icon: Shield, label: 'Secure Platform' },
];

const MOCK_TILES = [
  { label: 'Active Cases', value: '24' },
  { label: 'Offers Sent', value: '12' },
  { label: 'Funded', value: '$1.2M' },
];

const MOCK_CASES = [
  { name: 'Rodriguez v. GEICO', type: 'Auto Accident', amount: '$42,000', risk: 'Low Risk' },
  { name: 'Chen v. Metro Health', type: 'Med. Malpractice', amount: '$85,000', risk: 'Medium Risk' },
  { name: 'Adams v. Walmart', type: 'Slip & Fall', amount: '$21,000', risk: 'Low Risk' },
];

const BENEFITS = [
  { icon: Search, title: 'Curated Opportunities', desc: 'Browse pre-screened, attorney-submitted applications. No cold outreach or acquisition cost.' },
  { icon: FolderOpen, title: 'Organized Case Data', desc: 'Case type, claim state, requested amount, attorney details, and supporting documents — structured and ready for review.' },
  { icon: Scale, title: 'Attorney-Connected Workflow', desc: 'Every application is linked to a verified attorney. Communicate and collaborate directly through the platform.' },
  { icon: DollarSign, title: 'Built-In Offer Builder', desc: 'Configure advance amounts, fee structures, and risk tiers. Submit customized offers in minutes, not hours.' },
  { icon: BarChart3, title: 'Portfolio Dashboard', desc: 'Track active offers, funded cases, and pipeline metrics from a single, centralized view.' },
  { icon: Users, title: 'Transparent Collaboration', desc: 'Every status change and action is logged. Full visibility into application progress from intake to funding.' },
];

const STEPS = [
  { num: '1', icon: Search, title: 'Discover Opportunities', desc: 'Browse attorney-submitted, pre-screened funding applications filtered by case type, amount, and status.' },
  { num: '2', icon: FileCheck, title: 'Review Case Details', desc: 'Access organized case data, litigation history, attorney information, and uploaded documents — all in one place.' },
  { num: '3', icon: Send, title: 'Submit Your Offer', desc: 'Use the built-in offer builder to configure advance amounts, fee structures, and terms. Send offers directly to the attorney.' },
  { num: '4', icon: TrendingUp, title: 'Track & Manage', desc: 'Monitor offer status, funded cases, and pipeline performance from your dedicated funder dashboard.' },
];

const FIGURES = [
  { value: '24h', label: 'Avg. Review' },
  { value: '100%', label: 'Digital Workflow' },
  { value: '0', label: 'Email Chains' },
];

const POINTS = [
  { title: 'Structured Case Intake', desc: 'Every application arrives with organized fields — case type, claim state, funding range, and attorney details. No more parsing unstructured emails.' },
  { title: 'Direct Attorney Channel', desc: 'Communicate with the representing attorney through the platform. No phone tag or lost messages.' },
  { title: 'Centralized Document Access', desc: 'Supporting documents are uploaded and accessible from the case view. No searching through inboxes.' },
  { title: 'Offer Management Dashboard', desc: 'Build, send, and track all your offers in one place. See exactly where each opportunity stands.' },
  { title: 'Scalable Deal Flow', desc: 'Process more opportunities with less overhead. The platform handles intake, organization, and routing so you can focus on funding decisions.' },
  { title: 'Complete Audit Trail', desc: 'Every action — offers, status changes, communications — is logged and timestamped for your records.' },
];

const PREVIEW_APPS = [
  { name: 'Martinez v. State Farm', type: 'Auto Accident', amount: '$38,000', status: 'New' },
  { name: 'Johnson v. CVS Health', type: 'Slip & Fall', amount: '$22,500', status: 'Attorney Verified' },
  { name: 'Williams v. Dr. Patel', type: 'Med. Malpractice', amount: '$95,000', status: 'Docs Ready' },
];

const CASE_FIELDS = [
  { label: 'Case Type', value: 'Auto Accident' },
  { label: 'Amount', value: '$38,000' },
  { label: 'Attorney', value: 'R. Martinez, Esq.' },
  { label: 'Claim State', value: 'Florida' },
];

const CASE_DOCS = ['Police Report.pdf', 'Medical Records.pdf', 'Attorney Letter.pdf'];

const TRUST = [
  { icon: Shield, title: 'Enterprise-Grade Security', desc: 'Role-based access control, encrypted data transfer, and secure authentication protect every transaction on the platform.' },
  { icon: Scale, title: 'Purpose-Built for Legal Funding', desc: 'Not a generic loan marketplace. Every feature — from case intake to offer management — is designed around pre-settlement funding.' },
  { icon: Users, title: 'Clean Three-Party Collaboration', desc: 'Plaintiffs, attorneys, and funders each have dedicated portals with appropriate visibility and permissions.' },
  { icon: Clock, title: 'Faster Decision Cycles', desc: 'Organized case data and digital workflows mean you spend less time on intake and more time on evaluation.' },
];

export default function ForFunders() {
  usePageMeta(PAGE_META.forFunders);

  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="for-funders-page">
      <LandingHeader />

      <main>

      {/* ─── Hero ─── */}
      <section className={shared.section} data-testid="funders-hero">
        <div className={`${shared.wrap} ${styles.heroGrid}`}>
          <div>
            <span className={styles.badge}>For Funding Partners</span>
            <h1 className={styles.heroTitle}>
              Deploy Capital Faster with Organized, Attorney-Verified Cases
            </h1>
            <p className={styles.heroSub}>
              MyCaseLoan gives funders a modern platform to discover pre-screened
              opportunities, review structured case data, and submit offers — all without the
              overhead of fragmented intake and manual workflows.
            </p>
            <div className={styles.heroActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction}`}
                onClick={() => navigate('/signup', { state: { role: 'funder' } })}
                data-testid="hero-become-partner-btn"
              >
                Become a Funding Partner <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnQuiet}`}
                onClick={() => navigate('/login')}
                data-testid="hero-funder-login-btn"
              >
                Funder Login
              </button>
            </div>
            <ul className={styles.assurances}>
              {ASSURANCES.map((a) => (
                <li key={a.label} className={styles.assurance}>
                  <a.icon size={16} aria-hidden="true" />
                  {a.label}
                </li>
              ))}
            </ul>
          </div>

          {/* Illustrative funder dashboard */}
          <div data-testid="funders-hero-visual">
            <div className={styles.mock} aria-hidden="true">
              <div className={styles.mockHead}>
                <span className={styles.mockDot} />
                <span className={styles.mockDot} />
                <span className={styles.mockDot} />
                <span className={styles.mockTitle}>Funder Dashboard</span>
              </div>
              <div className={styles.mockBody}>
                <div className={styles.tiles}>
                  {MOCK_TILES.map((t) => (
                    <div key={t.label} className={styles.tile}>
                      <div className={`${styles.tileValue} mcl-num`}>{t.value}</div>
                      <div className={styles.tileLabel}>{t.label}</div>
                    </div>
                  ))}
                </div>
                {MOCK_CASES.map((c) => (
                  <div key={c.name} className={styles.mockRow}>
                    <div>
                      <div className={styles.rowName}>{c.name}</div>
                      <div className={styles.rowMeta}>{c.type}</div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={`${styles.rowValue} mcl-num`}>{c.amount}</div>
                      <div className={styles.rowStatus}>{c.risk}</div>
                    </div>
                  </div>
                ))}
                <div className={styles.mockActions}>
                  <div className={styles.mockBtn}>Review Cases</div>
                  <div className={`${styles.mockBtn} ${styles.mockBtnQuiet}`}>Build Offer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Benefits ─── */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="funders-benefits"
      >
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Why MyCaseLoan</span>
            <h2>Built for Modern Legal Funders</h2>
            <p>
              Everything you need to evaluate cases, deploy capital, and manage your
              portfolio — in one purpose-built platform.
            </p>
          </div>
          <div className={styles.grid3}>
            {BENEFITS.map((b) => (
              <div className={styles.card} key={b.title}>
                <div className={styles.iconBox}>
                  <b.icon size={22} aria-hidden="true" />
                </div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section className={shared.section} data-testid="funders-how-it-works">
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Simple Process</span>
            <h2>From Case Review to Capital Deployment</h2>
            <p>
              A streamlined four-step workflow designed to eliminate overhead and accelerate
              your funding decisions.
            </p>
          </div>
          <div className={styles.grid4}>
            {STEPS.map((s) => (
              <div className={`${styles.card} ${styles.step}`} key={s.num}>
                <div className={styles.stepNum}>{s.num}</div>
                <div className={styles.iconBox}>
                  <s.icon size={22} aria-hidden="true" />
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Platform value ─── */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="funders-platform-value"
      >
        <div className={`${shared.wrap} ${styles.split}`}>
          <div className={styles.splitHead}>
            <span className={shared.eyebrow}>The Difference</span>
            <h2>Replace Manual Workflows with a Modern Funding Platform</h2>
            <p>
              Traditional legal funding relies on fragmented email threads, unstructured
              intake, and disconnected spreadsheets. MyCaseLoan centralizes your entire
              operation.
            </p>
            <hr className={styles.rule} />
            <div className={styles.figures}>
              {FIGURES.map((f) => (
                <div key={f.label} className={styles.figure}>
                  <div className={`${styles.figureValue} mcl-num`}>{f.value}</div>
                  <div className={styles.figureLabel}>{f.label}</div>
                </div>
              ))}
            </div>
          </div>
          <ul className={styles.checks}>
            {POINTS.map((p) => (
              <li className={styles.check} key={p.title}>
                <span className={styles.checkIcon}>
                  <Check size={13} strokeWidth={3} aria-hidden="true" />
                </span>
                <div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Portal preview ─── */}
      <section className={shared.section} data-testid="funders-dashboard-preview">
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Platform Preview</span>
            <h2>Your Funder Portal at a Glance</h2>
            <p>
              A dedicated workspace for reviewing applications, building offers, and managing
              your legal funding portfolio.
            </p>
          </div>

          <div className={styles.grid3} aria-hidden="true">
            <div className={styles.preview}>
              <div className={styles.previewHead}>
                <Briefcase size={16} />
                Available Applications
              </div>
              <div className={styles.previewBody}>
                {PREVIEW_APPS.map((app) => (
                  <div key={app.name} className={styles.fileRow}>
                    <div>
                      <div className={styles.rowName}>{app.name}</div>
                      <div className={styles.rowMeta}>{app.type}</div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={`${styles.rowValue} mcl-num`}>{app.amount}</div>
                      <div className={styles.rowStatus}>{app.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.preview}>
              <div className={styles.previewHead}>
                <FileCheck size={16} />
                Case Details
              </div>
              <div className={styles.previewBody}>
                <div className={styles.inset}>
                  <div className={styles.insetHead}>
                    <span>MCL-3042</span>
                    <span className={styles.pill}>Verified</span>
                  </div>
                  <div className={styles.fields}>
                    {CASE_FIELDS.map((f) => (
                      <div key={f.label}>
                        <div className={styles.fieldLabel}>{f.label}</div>
                        <div className={styles.fieldValue}>{f.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.inset}>
                  <div className={styles.insetHead}>Documents</div>
                  {CASE_DOCS.map((doc) => (
                    <div key={doc} className={styles.task}>
                      <FolderOpen size={12} />
                      {doc}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.preview}>
              <div className={styles.previewHead}>
                <DollarSign size={16} />
                Offer Builder
              </div>
              <div className={styles.previewBody}>
                <div className={styles.field}>
                  <div className={styles.fieldLabel}>Advance Amount</div>
                  <div className={`${styles.fieldBox} mcl-num`}>$10,000</div>
                </div>
                <div className={styles.field}>
                  <div className={styles.fieldLabel}>Fee Structure</div>
                  <div className={`${styles.fieldBox} ${styles.fieldBoxSm} mcl-num`}>22% / year</div>
                </div>
                <div className={styles.field}>
                  <div className={styles.fieldLabel}>Risk Tier</div>
                  <div className={styles.meter}>
                    <div className={styles.meterTrack}>
                      <div className={styles.meterFill} style={{ width: '35%' }} />
                    </div>
                    <span className={styles.meterLabel}>Low</span>
                  </div>
                </div>
                <div className={styles.mockActions}>
                  <div className={styles.mockBtn}>Submit Offer</div>
                  <div className={`${styles.mockBtn} ${styles.mockBtnQuiet}`}>Save Draft</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trust ─── */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="funders-trust"
      >
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>Trust &amp; Security</span>
            <h2>A Platform Built for Serious Capital Deployment</h2>
            <p>
              MyCaseLoan is purpose-built for legal funding operations — with the security,
              compliance, and professionalism your business requires.
            </p>
          </div>
          <div className={styles.grid2}>
            {TRUST.map((t) => (
              <div className={`${styles.card} ${styles.trustCard}`} key={t.title}>
                <div className={styles.iconBox}>
                  <t.icon size={20} aria-hidden="true" />
                </div>
                <div>
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ─── */}
      <section
        className={`${shared.section} ${shared.sectionDark}`}
        data-testid="funders-final-cta"
      >
        <div className={shared.wrap}>
          <div className={styles.finalInner}>
            <h2>Ready to Modernize Your Legal Funding Operation?</h2>
            <p>
              Join MyCaseLoan and access organized, attorney-verified case flow. Deploy
              capital faster with less overhead and complete visibility.
            </p>
            <div className={styles.finalActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAccent}`}
                onClick={() => navigate('/signup', { state: { role: 'funder' } })}
                data-testid="final-cta-partner-btn"
              >
                Become a Funding Partner <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnOnDark}`}
                onClick={() => navigate('/login')}
                data-testid="final-cta-funder-login-btn"
              >
                Funder Login
              </button>
            </div>
          </div>
        </div>
      </section>
      </main>


      <LandingFooter />
    </div>
  );
}
